/**
 * When this browser last looked at the alert history.
 *
 * `localStorage` and not the server, deliberately: "have I read this" is a property of the person
 * sitting here, and putting it in the database would make one editor's reading mark the alert read
 * for the whole newsroom. The cost is that it does not follow them to another machine, which for a
 * badge is the right trade.
 */
const SEEN_KEY = 'pressentia.alerts.seenAt';

export function readAlertsSeenAt(): number {
  try {
    const stored = window.localStorage.getItem(SEEN_KEY);
    const parsed = stored ? Number(stored) : Number.NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    // Private-mode Safari throws on access rather than returning null.
    return 0;
  }
}

export function markAlertsSeen(at: number = Date.now()): void {
  try {
    window.localStorage.setItem(SEEN_KEY, String(at));
  } catch {
    // A badge that cannot remember being read is a cosmetic problem, not a failure worth raising.
  }
}

/**
 * The instant the unread count is measured from, stamping "now" the first time.
 *
 * Without the stamp a browser that has never opened the history has no floor, and the only honest
 * answers are "every alert ever raised" or nothing. Neither is an unread count, so the first
 * visit establishes one and reports zero.
 */
export function readOrStartAlertsSeenAt(): number {
  const seenAt = readAlertsSeenAt();
  if (seenAt) return seenAt;

  const now = Date.now();
  markAlertsSeen(now);
  return now;
}
